import { ref, shallowRef, watch, onMounted, type Ref, type ShallowRef } from 'vue';
import { injectLocal, provideLocal } from '@vueuse/core';
import type { userInfoParsed } from './types';
import {
  getBagGuidFromCookie,
  getIsSignedInFromCookie,
  getRawUserInfoFromLocalStorage,
  getUserGuidFromCookie,
  getUserIdFromCookie,
} from '~/utils/userSession';

import { userInfoParser } from './parsers';

type UserInfoParsed = userInfoParsed | null;

const USER_INFO: InjectionKey<{
  userId: Ref<string | undefined>;
  userGuid: Ref<string | undefined>;
  bagGuid: Ref<string | undefined>;
  userInfo: ShallowRef<UserInfoParsed>;
  isSignedIn: Ref<boolean>;
}> = Symbol.for('USER_INFO');

export const useUser = () => {
  const userInfo = injectLocal(USER_INFO);

  if (typeof userInfo === 'undefined') {
    throw new TypeError('useUser() is called without provider.');
  }

  return userInfo;
};

export const useUserProvide = () => {
  const userId = ref<string | undefined>('');
  const userGuid = ref<string | undefined>('');
  const bagGuid = ref<string | undefined>('');
  const isSignedIn = ref(false);
  const userInfo = shallowRef<UserInfoParsed>(null);

  const getTierName = (userInfo: UserInfoParsed): string => userInfo?.tierInfo?.tierName ?? '';
  const compareTierInfo = (oldUserInfo: UserInfoParsed, newUserInfo: UserInfoParsed): boolean =>
    getTierName(oldUserInfo) === getTierName(newUserInfo);

  watch(
    userId,
    (newId) => {
      // only if userInfo is null we should update from local storage value
      userInfo.value ??= userInfoParser(getRawUserInfoFromLocalStorage(newId));
    },
    { once: true },
  );

  // todos: see if this works with onBeforeMount to get data sooner on page load
  onMounted(() => {
    userId.value = getUserIdFromCookie();
    userGuid.value = getUserGuidFromCookie();
    bagGuid.value = getBagGuidFromCookie();
    isSignedIn.value = getIsSignedInFromCookie();

    window.addEventListener(
      'navLoyaltyDataUpdated',
      ({ detail = {} } = {}) => {
        // only updated if tier names are different, then apply fetched version (i.e. 'detail' obj)
        if (!compareTierInfo(userInfo.value, detail)) {
          userInfo.value = userInfoParser(detail);
        }
      },
      { once: true },
    );
  });

  provideLocal(USER_INFO, {
    userId,
    userGuid,
    bagGuid, // TODO: move to bag?
    userInfo,
    isSignedIn,
  });
};
